import { removeStorage } from 'src/app/shared/utils/safe-storage';

/**
 * The pre-refactor auth blob was written as `${appVersion}_auth` by builds running
 * appVersion `galactus_ui.15.0.0`. Pin that historical key here instead of deriving it
 * from the live `environment.appVersion`: the key to purge is fixed in history and must
 * NOT move when appVersion is bumped (e.g. the 15.0.1 bump that flushes the `_stat` blob),
 * or upgrading users would stop getting the stale JWT cleaned up.
 */
export const LEGACY_AUTH_STORAGE_KEY = 'galactus_ui.15.0.0_auth';

/**
 * One-shot migration: wipe pre-refactor auth blobs left in the user's browser by older builds.
 *
 * <p>Current code does not write any of these keys, but a user upgrading from a pre-refactor
 * build still has them sitting in storage. They're XSS-readable; an attacker who lands a script
 * could lift the old JWT (which is generally short-lived but not always expired). Clearing on
 * every boot is cheap and idempotent -- after the first run there's nothing to remove.
 *
 * <p>Safe to call repeatedly. Storage access goes through `safe-storage` because {@code
 * sessionStorage}/{@code localStorage} can throw in some browser modes (third-party-cookies-blocked,
 * private mode with strict settings). Guarding each call rather than the whole body is deliberate:
 * one try/catch around everything meant a throwing {@code removeItem} skipped the cookie expiry
 * below it, leaving a readable auth cookie behind in exactly the mode we most wanted it gone.
 *
 * <p>The cookie writes are guarded for the same reason (see {@link expireLegacyCookie}) -- they are
 * not storage, so `safe-storage` does not cover them, but they throw in the same browser modes and
 * this function runs from {@code AuthRefreshService.bootstrap()}, where an escaping exception takes
 * down app boot.
 */
export function purgeLegacyAuthStorage(): void {
  // Pre-refactor `${appVersion}_auth` -- carried the raw access JWT in session/local storage.
  // Pinned to the historical key (see LEGACY_AUTH_STORAGE_KEY) so appVersion bumps don't abandon it.
  removeStorage('session', LEGACY_AUTH_STORAGE_KEY);
  removeStorage('local', LEGACY_AUTH_STORAGE_KEY);

  // Pre-refactor cookie carrying auth params. Expire on the paths older builds used
  // (root + /auth) so nothing readable survives.
  removeStorage('local', 'paramsToauthgAlactus');
  expireLegacyCookie('/');
  expireLegacyCookie('/auth');
}

/**
 * Expire the pre-refactor auth cookie on one path, swallowing a refusal.
 *
 * <p>`document.cookie` throws a {@code SecurityError} in a sandboxed iframe or an opaque-origin
 * document -- the same modes that make storage throw. Guarded per path rather than per pair for the
 * reason the whole-body try/catch was removed: a throw on `/` must not skip `/auth`.
 *
 * <p>Swallowing is safe: the restriction that blocks the write blocks any read of the same cookie,
 * so there is nothing accessible left to leak in that context.
 */
function expireLegacyCookie(path: string): void {
  try {
    document.cookie = `paramsToauthgAlactus=; Max-Age=0; path=${path}`;
  } catch {
    // Cookie access refused by the browser -- best-effort purge, nothing readable survives anyway.
  }
}
